<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'

import type { Props } from './types'

const props = defineProps<Props>()

const { public: { googleApiKey } } = useRuntimeConfig()
const { t } = useI18n()
const emit = defineEmits(['openGallery'])

const staticMapSection = ref()
const isSectionVisible = ref(false)
const observer = ref<IntersectionObserver>()

const googleMapsBaseUrl = 'https://maps.googleapis.com/maps/api/staticmap?'
const googleMapsIconUrl = 'https://assets.lahaus.com/static/images/logos/Pin.png'

const getMapOptions = (apiKey: string) => `&zoom=15&size=1080x150&scale=2&key=${apiKey}`
const getLocationParams = (lat: number, lng: number) => `bottom=${lat},${lng}`
const getMapIcon = (lat: number, lng: number) => `&markers=icon:${googleMapsIconUrl}%7Cscale:2%7Canchor:bottom%7C${lat},${lng}`

const generateMapUrl = (lat: number, lng: number, apiKey: string): string => {
  return `${googleMapsBaseUrl}${getLocationParams(lat, lng)}${getMapOptions(apiKey)}${getMapIcon(lat, lng)}`
}

const fullUrlMap = generateMapUrl(props.lat, props.lng, googleApiKey)

function observeElementVisibility (
  element: Ref<HTMLElement | null>,
  isVisible: Ref<boolean>,
  threshold: number = 0.1
) {
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        isVisible.value = true
      }
    },
    {
      threshold
    }
  )

  if (element.value) {
    observer.observe(element.value)
  }

  return observer
}

onMounted(() => {
  observer.value = observeElementVisibility(staticMapSection, isSectionVisible)
})

function disconnectObserver (observer: Ref<IntersectionObserver | undefined>) {
  if (observer.value) {
    observer.value.disconnect()
  }
}

onUnmounted(() => {
  disconnectObserver(observer)
})
</script>

<script lang="ts">
export default {
  name: 'AppMapStatic'
}
</script>

<template>
  <div
    v-if="lat && lng"
    ref="staticMapSection"
    class="flex flex-col lg:flex-row map-container">
    <div class="location-container flex flex-col justify-between py-32 px-24 lg:p-48 items-center">
      <slot
        :is-section-visible="isSectionVisible"
        name="location-information" />
    </div>

    <button
      v-if="isSectionVisible"
      :id="id"
      :data-lh-id="id"
      :aria-label="t('ariaLabel')"
      class="map-button w-full overflow-hidden relative"
      :class="buttonStyles"
      @click="emit('openGallery', 'location')">
      <figure class="pointer-events-none h-full">
        <img
          :src="fullUrlMap"
          :alt="`static map ${locationName}`"
          :class="imgStyles"
          class="w-full object-cover pointer-events-none">
      </figure>
    </button>
  </div>
</template>

<i18n src="./i18n.json" lang="json" />

<style lang="scss">
.lh-roomie-v2 {
  .map-container {
    @apply rounded-24 overflow-hidden;

    background: #004132;

    .map-button {
      min-height: 34.8rem;

      img {
        min-height: 34.8rem;
      }
    }

    .address {
      color: #E6FA41;
    }

    .see-location-button {
      @apply cta-16;

      border-radius: 100px;
      border: 1px solid #0041324d;
      background: white;
      color: #004132;
    }

    .location-container {
      max-width: 45rem;
    }
  }
}
</style>
